import { HelpIcon, Table } from '@dixa/lib-shared-habla';
import { FC, useCallback, useRef } from 'react';

import { useScrollToLastChild } from 'hooks/useScrollToLastChild';

import { Payload, PhoneNumber } from '../../nodeTypes/forward';
import { CallPattern } from '../../types';

import styles from './forwardTable.module.scss';
import ForwardTableRow from './forwardTableRow';

type Props = {
  forward: Payload;
  onUpdateNumbers: (numbers: PhoneNumber[]) => void;
};

const ForwardTable: FC<Props> = ({ forward, onUpdateNumbers }) => {
  const tableBody = useRef<any>(null);

  const { numbers, callPattern } = forward;

  useScrollToLastChild<Payload['numbers']>(tableBody, numbers);

  const onMoveNumber = useCallback(
    (dragIndex, hoverIndex) => {
      const newNumbers = [...numbers];

      const draggedElement = numbers[dragIndex];
      const hoveredElement = numbers[hoverIndex];

      newNumbers[dragIndex] = hoveredElement;
      newNumbers[hoverIndex] = draggedElement;

      onUpdateNumbers(newNumbers);
    },
    [numbers, onUpdateNumbers],
  );

  const onNumberUpdate = useCallback(
    (index) => (number: PhoneNumber) => {
      const newNumbers = [...numbers];
      const { timeout: prevTimeout } = numbers[index];
      const { timeout } = number;

      if (
        callPattern === CallPattern.FirstToPickUp &&
        prevTimeout !== timeout
      ) {
        newNumbers.forEach((number) => (number.timeout = timeout));
      }

      newNumbers[index] = number;

      onUpdateNumbers(newNumbers);
    },
    [callPattern, numbers, onUpdateNumbers],
  );

  const onNumberRemove = useCallback(
    (index) => () => {
      const newNumbers = new Set(numbers);

      newNumbers.delete(numbers[index]);

      onUpdateNumbers([...newNumbers]);
    },
    [numbers, onUpdateNumbers],
  );

  return (
    <Table stickyHeader wrapperProps={{ className: styles.tableWrapper }}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th />
          <Table.Th>Type</Table.Th>
          <Table.Th>
            Forward to{' '}
            <HelpIcon
              className={styles.helpIcon}
              text="Type in the number that calls should be forwarded to. Remember to add the country code (e.g. +12058321018)."
            />
          </Table.Th>
          <Table.Th>
            Ringing time{' '}
            <HelpIcon
              className={styles.helpIcon}
              text={
                callPattern === CallPattern.FirstToPickUp
                  ? 'The phone call is offered to all phone numbers (agents) until one picks up. If no one picks up or time runs out, the phone call is forwarded to the next action in this flow.'
                  : "The phone call is offered to the first phone number (agent) on the list. If that agent doesn't pick up in time, it's offered to the next number on the list, and the next, and so on. If none of the agents on the list pick up, the phone call is forwarded to the next action in this flow."
              }
            />
          </Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody className={styles.tableBody} ref={tableBody}>
        {numbers.map((number, index) => (
          <ForwardTableRow
            key={number.id}
            number={number}
            numbers={numbers}
            index={index}
            onMoveNumber={onMoveNumber}
            onNumberUpdate={onNumberUpdate(index)}
            onNumberRemove={onNumberRemove(index)}
          />
        ))}
      </Table.Tbody>
    </Table>
  );
};

export default ForwardTable;
